import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * One key per persisted list. An enum rather than a bare `string` so a typo cannot silently create a
 * second, empty slot for a table that thought it was restoring one.
 */
export enum TableKeys {
  FABRIC = 'FABRIC',
  COLLECTION = 'COLLECTION',
  FAVORITE = 'FAVORITE',
  QUOTATION_REQUEST = 'QUOTATION_REQUEST',
  ORDER = 'ORDER',
  DEVELOPMENT_REQUEST = 'DEVELOPMENT_REQUEST',
  DEVELOPMENT_REQUEST_SEARCH = 'DEVELOPMENT_REQUEST_SEARCH',
}

export interface TableState {
  page: number;
  /**
   * Optional, because a state can also be *seeded* for a list that has never been opened — see
   * {@link DatatableService.seedSearch}. Absent means "whatever page size that list defaults to".
   */
  pageSize?: number;
  search?: string;
  filters?: unknown;
  /**
   * Whatever a page needs to put its *controls* back the way they were, when that cannot be read off
   * `filters` alone — a child filter panel's own value object, say. `filters` is what goes to the API;
   * this is what the UI needs to agree with it. Opaque to the service.
   */
  viewState?: unknown;
  /**
   * Offset of the layout's scroll container when the list was left, for pages that opt in with
   * `persistScroll`. Kept fresh while the user scrolls rather than written on the way out: the state is
   * already gone by the time a leaving component is destroyed.
   */
  scrollTop?: number;
}

/**
 * Remembers where each list was — page, size, search and filters — so opening a record and coming back
 * returns you to the list you left rather than to page 1 of everything.
 *
 * **In memory, for the life of the tab.** A refresh starts clean, which is deliberate: the alternative
 * is `sessionStorage`, and JSON would turn a `Date` in a filter into a string that silently no longer
 * matches the control that wrote it.
 *
 * **Cleared when you leave the feature area.** Keeping a list filtered forever is how someone returns an
 * hour later to an apparently empty screen with no idea why. Navigating from `/quotations` to a record
 * *under* `/quotations` keeps the state; navigating to `/fabrics` drops it. Same rule, and the same
 * shape, as the backoffice's `moduleTables`/`getModuleFromUrl`.
 */
@Injectable({
  providedIn: 'root',
})
export class DatatableService {
  private readonly state = new Map<TableKeys, TableState>();

  /**
   * Which tables belong to which feature area, keyed by the module names `getModuleFromUrl` returns.
   * A key missing here is never cleared and so stays filtered for the life of the tab, so a new list
   * registers here as well as in {@link TableKeys}.
   */
  private readonly moduleTables: Record<string, TableKeys[]> = {
    FABRIC_MODULE: [TableKeys.FABRIC],
    COLLECTION_MODULE: [TableKeys.COLLECTION],
    FAVORITE_MODULE: [TableKeys.FAVORITE],
    QUOTATION_MODULE: [TableKeys.QUOTATION_REQUEST],
    ORDER_MODULE: [TableKeys.ORDER],
    DEVELOPMENT_REQUEST_MODULE: [TableKeys.DEVELOPMENT_REQUEST, TableKeys.DEVELOPMENT_REQUEST_SEARCH],
  };

  // Default table configurations
  public defaultPagination = {
    pageIndex: 1,
    pageSize: 10,
    total: 0,
  };

  public defaultLoading = false;

  /** The area just navigated away from, whose tables are no longer allowed to write — see `setState`. */
  private leavingModule: string | null = null;

  constructor(private readonly router: Router) {
    let currentModule = this.getModuleFromUrl(this.router.url);

    // NavigationStart, not End: the list component of the page being entered may read its state during
    // its own construction, so the clear has to have happened before the new route is instantiated.
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event) => {
      const nextModule = this.getModuleFromUrl((event as NavigationStart).url);
      const leaving = nextModule !== currentModule;

      if (leaving) {
        this.clearModule(currentModule);
      }

      this.leavingModule = leaving ? currentModule : null;
      currentModule = nextModule;
    });
  }

  public defaultSortFn = (a: any, b: any) => a.localeCompare(b);

  public defaultFilterFn = (list: string[], item: string) => list.some((filter) => filter === item);

  getState(tableKey: TableKeys): TableState | undefined {
    const saved = this.state.get(tableKey);
    // Copied on the way out as well as in: the caller edits `filters` in place (that is how the
    // ngModel-bound filter panels work), and handing back the stored object would let a later edit
    // rewrite history — including after the user pressed "Clear".
    return saved ? { ...saved, filters: this.copyFilters(saved.filters) } : undefined;
  }

  setState(tableKey: TableKeys, value: TableState): void {
    // A list on its way out must not write. The clear runs at NavigationStart while the page being left
    // lives on for a moment longer, so a late write — an in-flight response, or the tail of an inertial
    // scroll — would resurrect the state just dropped, and nothing would come to clear it again.
    if (this.leavingModule && (this.moduleTables[this.leavingModule] ?? []).includes(tableKey)) {
      return;
    }

    this.state.set(tableKey, { ...value, filters: this.copyFilters(value.filters) });
  }

  /**
   * Opens a list on a search it has not been opened with yet.
   *
   * For a reader who reached a record without going through its list — a suggestion panel, a deep
   * link — so that coming back to the list, by whatever route, finds the search the record was
   * picked from rather than the whole catalogue. Deliberately replaces any state already stored:
   * a search just run is what the reader is working on.
   *
   * Page size is left out so the list keeps its own default.
   */
  seedSearch(tableKey: TableKeys, search: string): void {
    this.setState(tableKey, { page: 1, search });
  }

  clearState(tableKey: TableKeys): void {
    this.state.delete(tableKey);
  }

  clearModule(module: string): void {
    (this.moduleTables[module] ?? []).forEach((key) => this.clearState(key));
  }

  /**
   * The feature area a URL belongs to. Anything deeper than the area root — `/quotations/:uuid` — maps to
   * the same module as the list, which is exactly what makes "open a record and come back" work.
   */
  private getModuleFromUrl(url: string): string {
    if (url.startsWith('/fabrics')) return 'FABRIC_MODULE';
    if (url.startsWith('/collections')) return 'COLLECTION_MODULE';
    if (url.startsWith('/favorites')) return 'FAVORITE_MODULE';
    if (url.startsWith('/quotations')) return 'QUOTATION_MODULE';
    if (url.startsWith('/orders')) return 'ORDER_MODULE';
    if (url.startsWith('/development-requests')) return 'DEVELOPMENT_REQUEST_MODULE';
    return 'UNKNOWN_MODULE';
  }

  /** Shallow copy, enough to stop the stored snapshot aliasing the live filter object. */
  private copyFilters(filters: unknown): unknown {
    return filters && typeof filters === 'object' ? { ...(filters as Record<string, unknown>) } : filters;
  }

  // Method to reset table configurations
  public resetPagination() {
    return { ...this.defaultPagination };
  }
}
